/**
 * Merchant sandbox entry.
 *
 * A dependency-light, framework-free page that integrates the published
 * `@sunbay/checkout-sdk-js` exactly the way a real merchant would: install the
 * package, import `createCheckout`, mount an `expressCheckout` element (Apple Pay
 * / Google Pay), a `card` element and `address` elements, and drive a payment to
 * a terminal outcome.
 *
 * No card data ever touches this page — entry happens inside the SDK's hosted
 * iframes (PCI isolation). This page only orchestrates.
 */
import { createCheckout } from '@sunbay/checkout-sdk-js';
import { createSession } from './session.js';
import './styles.css';

/* ------------------------------- DOM helpers ------------------------------ */

const $ = (id) => document.getElementById(id);

/** Instance-level events surfaced in the log. */
const INSTANCE_EVENTS = ['paymentResult', 'paymentStateChange', 'error'];
/** Element-level events surfaced in the log. */
const ELEMENT_EVENTS = [
    'elementReady',
    'loadError',
    'ready',
    'change',
    'focus',
    'blur',
    'expressAvailabilityChange',
];

/* --------------------------------- Logger --------------------------------- */

const logEl = $('log');

function log(label, data) {
    const time = new Date().toLocaleTimeString();
    const line =
        data === undefined ? `[${time}] ${label}` : `[${time}] ${label} ${safeStringify(data)}`;
    logEl.textContent += (logEl.textContent ? '\n' : '') + line;
    logEl.scrollTop = logEl.scrollHeight;
}

function safeStringify(data) {
    if (data instanceof Error) return data.message;
    try {
        return JSON.stringify(data);
    } catch {
        return String(data);
    }
}

function setLogExpanded(expanded) {
    const dock = $('logdock');
    dock.classList.toggle('expanded', expanded);
    dock.classList.toggle('collapsed', !expanded);
    const toggle = $('logToggle');
    toggle.textContent = expanded ? 'Collapse' : 'Expand';
    toggle.setAttribute('aria-expanded', String(expanded));
}

/* ------------------------------ Status helpers ---------------------------- */

function setStatus(el, text, kind) {
    el.textContent = text;
    el.classList.remove('ok', 'err');
    if (kind) el.classList.add(kind);
}

/* ---------------------------- Mutable page state -------------------------- */

const state = {
    instance: null,
    express: null,
    card: null,
    addressBilling: null,
    addressShipping: null,
};

/* ------------------------- Options from the controls ---------------------- */

/** Read createCheckout options from the session + global option controls. */
function readCreateOptions() {
    const sessionId = $('sessionId').value.trim();
    if (!sessionId) {
        throw new Error('Session id is required — create one or paste it into the field first.');
    }

    const opts = { sessionId, env: $('env').value /* 'dev' | 'test' | 'prod' */ };

    // Embedded sessions require the per-session client secret from create-session.
    // Read it from the field so both "+ Session" (auto-filled) and paste paths work.
    const checkoutClientSecret = $('checkoutClientSecret').value.trim();
    if (checkoutClientSecret) {
        opts.checkoutClientSecret = checkoutClientSecret;
    }

    const locale = $('locale').value.trim();
    if (locale) opts.locale = locale;

    const appearance = readAppearance();
    if (appearance) opts.appearance = appearance;

    const paymentMethods = readPaymentMethodFilter();
    if (paymentMethods) opts.paymentMethods = paymentMethods;

    return opts;
}

/** Build the global Appearance option (currently just colorScheme). */
function readAppearance() {
    const colorScheme = $('colorScheme').value;
    if (!colorScheme) return undefined;
    return { colorScheme };
}

/** Build the PaymentMethodFilter (subtractive hide + reordering) from the two inputs. */
function readPaymentMethodFilter() {
    const hide = splitCsv($('pmHide').value);
    const order = splitCsv($('pmOrder').value);
    const filter = {};
    if (hide.length) filter.hide = hide;
    if (order.length) filter.order = order;
    return Object.keys(filter).length ? filter : undefined;
}

/** Split a comma-separated input into a trimmed, non-empty list. */
function splitCsv(value) {
    return value
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
}

/**
 * Build ExpressCheckoutElementOptions from the wallet controls.
 * A wallet set to "force hidden" => false; otherwise an object carrying any
 * chosen buttonType / buttonTheme, or omitted entirely when nothing is set
 * (so the SDK/hosted side applies its defaults).
 */
function readExpressOptions() {
    const options = {};
    const paymentMethods = {};

    paymentMethods.applePay = readWallet('applePay', 'applePayButtonType', 'applePayButtonTheme');
    paymentMethods.googlePay = readWallet('googlePay', 'googlePayButtonType', 'googlePayButtonTheme');
    options.paymentMethods = paymentMethods;

    const buttonHeight = Number($('buttonHeight').value);
    if (Number.isFinite(buttonHeight) && buttonHeight > 0) {
        options.buttonHeight = buttonHeight;
    }

    const maxColumns = Number($('maxColumns').value);
    if (Number.isFinite(maxColumns) && maxColumns > 0) {
        options.layout = { maxColumns };
    }

    return options;
}

/** Resolve a single wallet's option: false | {buttonType?,buttonTheme?} | true. */
function readWallet(showId, typeId, themeId) {
    if ($(showId).value === 'false') return false;
    const cfg = {};
    const type = $(typeId).value;
    const theme = $(themeId).value;
    if (type) cfg.buttonType = type;
    if (theme) cfg.buttonTheme = theme;
    // When no fine-grained option is chosen, `true` means auto-show with defaults.
    return Object.keys(cfg).length > 0 ? cfg : true;
}

/* ----------------------------- Event wiring ------------------------------- */

function subscribeInstanceEvents(instance) {
    for (const name of INSTANCE_EVENTS) {
        instance.on(name, (payload) => log(`instance · ${name}`, payload));
    }
}

function subscribeElementEvents(label, element) {
    for (const name of ELEMENT_EVENTS) {
        element.on(name, (payload) => log(`${label} · ${name}`, payload));
    }
}

/* --------------------------- Session creation ----------------------------- */

/** Read the new-session parameters (amount / currency / address collection). */
function readSessionParams() {
    const input = {
        currency: $('sessCurrency').value,
        collectBillingAddress: $('sessCollectBilling').checked,
        collectShippingAddress: $('sessCollectShipping').checked,
    };
    const amount = Number($('sessAmount').value);
    if (Number.isFinite(amount) && amount >= 0 && $('sessAmount').value.trim() !== '') {
        input.amount = amount;
    }
    return input;
}

async function createSessionAndFill() {
    const btn = $('btnCreateSession');
    btn.disabled = true;
    setStatus($('sessionCreateStatus'), 'Creating session…');
    const params = readSessionParams();
    log('create-session', params);
    try {
        const data = await createSession(params);
        $('sessionId').value = data.sessionId;
        // Fill the client secret field for createCheckout (embedded sessions).
        // Do not log it — only the sessionId is surfaced.
        $('checkoutClientSecret').value = data.checkoutClientSecret ?? '';
        setStatus($('sessionCreateStatus'), `Session ready · ${data.sessionId}`, 'ok');
        log('create-session ready', { sessionId: data.sessionId });
    } catch (err) {
        setStatus($('sessionCreateStatus'), err?.message || 'create-session failed', 'err');
        log('create-session error', err);
    } finally {
        btn.disabled = false;
    }
}

/* ------------------------------- Lifecycle -------------------------------- */

async function createInstance() {
    let opts;
    try {
        opts = readCreateOptions();
    } catch (err) {
        setStatus($('createStatus'), err.message, 'err');
        return;
    }

    teardownInstance(); // start clean if a previous instance exists
    setStatus($('createStatus'), 'Creating…');
    log('createCheckout', opts);

    try {
        const instance = await createCheckout(opts);
        state.instance = instance;
        subscribeInstanceEvents(instance);
        setStatus($('createStatus'), `Ready · ${instance.checkoutId}`, 'ok');
        log('createCheckout ready', { checkoutId: instance.checkoutId, capabilities: instance.capabilities });

        // Enable downstream controls.
        $('btnDestroy').disabled = false;
        $('btnMountExpress').disabled = false;
        $('btnMountCard').disabled = false;
        $('btnMountAddressBilling').disabled = false;
        $('btnMountAddressShipping').disabled = false;
        $('btnConfirm').disabled = false;
        $('btnStatus').disabled = false;

        // Auto-mount express so wallet buttons appear immediately.
        await mountExpress();
    } catch (err) {
        setStatus($('createStatus'), err?.message || 'createCheckout failed', 'err');
        log('createCheckout error', err);
    }
}

async function mountExpress() {
    if (!state.instance) return;
    const slot = $('slot-express');

    // Re-mount: destroy the previous express element and clear its slot so the
    // singleton exclusion slot is freed before creating a new one.
    if (state.express) {
        state.express.destroy();
        state.express = null;
    }
    slot.innerHTML = '';

    let options;
    try {
        options = readExpressOptions();
    } catch (err) {
        setStatus($('expressNote'), err.message, 'err');
        return;
    }

    setStatus($('expressNote'), 'Mounting…');
    log('create expressCheckout', options);
    try {
        const express = state.instance.create('expressCheckout', options);
        state.express = express;
        subscribeElementEvents('express', express);
        await express.mount('#slot-express');
        const wallets = express.getAvailableWallets();
        setStatus($('expressNote'), `Mounted · wallets=${safeStringify(wallets)}`, 'ok');
        log('express mounted · availableWallets', wallets);
    } catch (err) {
        setStatus($('expressNote'), err?.message || 'express mount failed', 'err');
        log('express mount error', err);
    }
}

async function mountCard() {
    if (!state.instance) return;
    const slot = $('slot-card');

    if (state.card) {
        state.card.destroy();
        state.card = null;
    }
    slot.innerHTML = '';

    const options = { showPostalCode: $('cardPostal').checked };
    log('create card', options);
    try {
        const card = state.instance.create('card', options);
        state.card = card;
        subscribeElementEvents('card', card);
        await card.mount('#slot-card');
        log('card mounted');
    } catch (err) {
        log('card mount error', err);
    }
}

/**
 * Mount an address Element for a given mode ('billing' | 'shipping').
 * Addresses are mutually exclusive by mode, so we destroy the previous
 * same-mode element and free its slot before creating a new one.
 */
async function mountAddress(mode) {
    if (!state.instance) return;
    const isBilling = mode === 'billing';
    const slot = $(isBilling ? 'slot-address-billing' : 'slot-address-shipping');
    const note = $(isBilling ? 'addressBillingNote' : 'addressShippingNote');
    const key = isBilling ? 'addressBilling' : 'addressShipping';

    if (state[key]) {
        state[key].destroy();
        state[key] = null;
    }
    slot.innerHTML = '';

    const options = { mode };
    setStatus(note, 'Mounting…');
    log(`create address · ${mode}`, options);
    try {
        const address = state.instance.create('address', options);
        state[key] = address;
        subscribeElementEvents(`address:${mode}`, address);
        await address.mount(isBilling ? '#slot-address-billing' : '#slot-address-shipping');
        setStatus(note, 'Mounted', 'ok');
        log(`address ${mode} mounted`);
    } catch (err) {
        setStatus(note, err?.message || 'address mount failed', 'err');
        log(`address ${mode} mount error`, err);
    }
}

async function confirmPayment() {
    if (!state.instance) return;
    setStatus($('confirmStatus'), 'Confirming…');
    $('outcome').textContent = '';
    log('confirm()');
    try {
        const outcome = await state.instance.confirm();
        setStatus($('confirmStatus'), `Outcome: ${outcome?.status ?? 'done'}`, 'ok');
        $('outcome').textContent = safeStringify(outcome);
        log('confirm resolved', outcome);
    } catch (err) {
        setStatus($('confirmStatus'), err?.message || 'confirm failed', 'err');
        $('outcome').textContent = safeStringify(err);
        log('confirm error', err);
    }
}

async function getPaymentStatus() {
    if (!state.instance) return;
    try {
        const status = await state.instance.getPaymentStatus();
        setStatus($('confirmStatus'), `Payment status: ${safeStringify(status)}`, 'ok');
        log('getPaymentStatus', status);
    } catch (err) {
        setStatus($('confirmStatus'), err?.message || 'getPaymentStatus failed', 'err');
        log('getPaymentStatus error', err);
    }
}

function teardownInstance() {
    if (state.instance) {
        try {
            state.instance.destroy();
        } catch {
            /* idempotent */
        }
    }
    state.instance = null;
    state.express = null;
    state.card = null;
    state.addressBilling = null;
    state.addressShipping = null;
    $('slot-express').innerHTML = '';
    $('slot-card').innerHTML = '';
    $('slot-address-billing').innerHTML = '';
    $('slot-address-shipping').innerHTML = '';

    $('btnDestroy').disabled = true;
    $('btnMountExpress').disabled = true;
    $('btnMountCard').disabled = true;
    $('btnMountAddressBilling').disabled = true;
    $('btnMountAddressShipping').disabled = true;
    $('btnConfirm').disabled = true;
    $('btnStatus').disabled = true;
    setStatus($('expressNote'), '');
    setStatus($('addressBillingNote'), '');
    setStatus($('addressShippingNote'), '');
    setStatus($('confirmStatus'), '');
}

/* ------------------------------ Event binding ----------------------------- */

function bindUi() {
    $('btnCreateSession').addEventListener('click', createSessionAndFill);

    $('btnCreate').addEventListener('click', createInstance);
    $('btnDestroy').addEventListener('click', () => {
        teardownInstance();
        setStatus($('createStatus'), 'Destroyed');
        log('instance destroyed');
    });
    $('btnMountExpress').addEventListener('click', mountExpress);
    $('btnMountCard').addEventListener('click', mountCard);
    $('btnMountAddressBilling').addEventListener('click', () => mountAddress('billing'));
    $('btnMountAddressShipping').addEventListener('click', () => mountAddress('shipping'));
    $('btnConfirm').addEventListener('click', confirmPayment);
    $('btnStatus').addEventListener('click', getPaymentStatus);

    $('logToggle').addEventListener('click', () =>
        setLogExpanded($('logdock').classList.contains('collapsed'))
    );
    $('logClear').addEventListener('click', () => {
        logEl.textContent = '';
    });
}

/** Pre-fill the session id field from the `s` query parameter, if present. */
function applySessionIdFromQuery() {
    const s = new URLSearchParams(window.location.search).get('s');
    if (s && s.trim()) {
        $('sessionId').value = s.trim();
        log('session id pre-filled from ?s= query parameter');
    }
}

bindUi();
applySessionIdFromQuery();
log('page loaded · create or paste a session id, set options, then Create checkout');
